'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import { useRemoveSplash } from '@/hooks/useRemoveSplash';
import { useAuthStore } from '@/stores/authStore';
import { ScreenContent } from '@/screen';
import { LoginMethods } from '@/components/LoginMethods';
import { AuthScreenShell } from '../AuthScreenShell';
import translations from './LoginScreen.translations';

export const LoginScreen = () => {
  const { t } = useLocalTranslation(translations);
  const router = useRouter();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [error, setError] = useState<string | null>(null);

  useRemoveSplash();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const trackingKeys = { ref: 'ref', yclid: 'yclid', cid: 'cid', aid: 'aid', ya: 'ya', utm_term: 'utmterm' };
    const segments: string[] = [];
    let hasTracking = false;
    for (const [urlKey, segKey] of Object.entries(trackingKeys)) {
      const val = params.get(urlKey);
      if (val) {
        segments.push(`${segKey}_${val}`);
        params.delete(urlKey);
        hasTracking = true;
      }
    }
    if (hasTracking) {
      const startParam = segments.join('--');
      const maxAge = 30 * 24 * 60 * 60;
      document.cookie = `shot_start_param=${encodeURIComponent(startParam)}; path=/; max-age=${maxAge}; SameSite=Lax`;
      const clean = window.location.pathname + (params.toString() ? `?${params}` : '');
      window.history.replaceState({}, '', clean);
    }

    const urlError = params.get('error');
    if (urlError) {
      setError(t('loginError'));
      params.delete('error');
      const clean = window.location.pathname + (params.toString() ? `?${params}` : '');
      window.history.replaceState({}, '', clean);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('next');
    let candidate = fromQuery;
    if (!candidate) {
      candidate = sessionStorage.getItem('shot_login_next');
      sessionStorage.removeItem('shot_login_next');
    }
    const next =
      candidate && candidate.startsWith('/') && !candidate.startsWith('//')
        ? candidate
        : '/';
    router.replace(next);
  }, [isAuthenticated, router]);

  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const next = params?.get('next') ?? undefined;

  return (
    <ScreenContent>
      <AuthScreenShell align="stretch" error={error}>
        <LoginMethods next={next} />
      </AuthScreenShell>
    </ScreenContent>
  );
};
